import { useState, useEffect } from "react";
import { captureException } from "@/lib/sentry";

const PRIVATE_STORAGE_PREFIXES = [
  "/storage/booking-references/",
  "/storage/healed-photos/",
  "/storage/after-photos/",
  "/storage/support/",
  "/storage/release-forms/paper/",
  "/storage/chat-images/",
  "/storage/contacts/",
  "/storage/public/contacts/",
];

export function isPrivateStoragePath(src?: string | null): boolean {
  return !!src && PRIVATE_STORAGE_PREFIXES.some(p => src.split("?")[0].startsWith(p));
}

export function usePrivateUrl(
  src: string | null | undefined,
  headers: Record<string, string>,
): { blobUrl: string | null; loading: boolean; error: boolean } {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const adminKey = headers["x-admin-key"] ?? "";
  const manageToken = headers["x-manage-token"] ?? "";
  const ownerToken = headers["x-owner-token"] ?? "";
  const messageToken = headers["x-message-token"] ?? "";
  const authorization = headers["Authorization"] ?? headers["authorization"] ?? "";

  useEffect(() => {
    if (!src) {
      setBlobUrl(null);
      setError(false);
      return;
    }

    if (!isPrivateStoragePath(src)) {
      setBlobUrl(src);
      setError(false);
      return;
    }

    if (!adminKey && !manageToken && !ownerToken && !messageToken && !authorization) {
      setError(true);
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;
    const plainPath = src.split("?")[0];

    setLoading(true);
    setError(false);
    setBlobUrl(null);

    fetch(plainPath, { headers })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then(blob => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
        setLoading(false);
      })
      .catch((err) => {
        captureException(err);
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src, adminKey, manageToken, ownerToken, messageToken, authorization]);

  return { blobUrl, loading, error };
}

export async function openPrivateUrl(
  src: string,
  headers: Record<string, string>,
): Promise<void> {
  if (!isPrivateStoragePath(src)) {
    window.open(src, "_blank", "noopener,noreferrer");
    return;
  }
  const plainPath = src.split("?")[0];
  try {
    const res = await fetch(plainPath, { headers });
    if (!res.ok) return;
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    window.open(objectUrl, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(objectUrl), 90_000);
  } catch (err) {
    captureException(err);
  }
}

export interface PrivateImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src"> {
  src: string | null | undefined;
  headers?: Record<string, string>;
}

export function PrivateImage({
  src,
  headers = {},
  alt,
  style,
  className,
  ...rest
}: PrivateImageProps) {
  const { blobUrl, loading, error } = usePrivateUrl(src, headers);

  if (!src) return null;

  if (!isPrivateStoragePath(src)) {
    return (
      <img src={src} alt={alt} style={style} className={className} {...rest} />
    );
  }

  if (error || (!loading && !blobUrl)) {
    return (
      <div
        style={{ background: "#1a1a2e", ...style }}
        className={className}
      />
    );
  }

  if (loading || !blobUrl) {
    return (
      <div
        style={{ background: "#111", ...style }}
        className={className}
      />
    );
  }

  return (
    <img src={blobUrl} alt={alt} style={style} className={className} {...rest} />
  );
}
